import { ReactElement, useCallback } from 'react';

import { AutocompleteProps, InputLabelProps } from '@mui/material';
import {
  FieldTitle,
  Identifier,
  InputHelperText,
  RaRecord,
  Validator,
  useChoicesContext,
  useInput,
} from 'react-admin';
import { Autocomplete } from 'shared/mui/Autocomplete/Autocomplete';
import { Checkbox } from 'shared/mui/Checkbox/Checkbox';
import { Chip } from 'shared/mui/Chips';
import { TextField } from 'shared/mui/TextField/TextField';

import { useAutocompleteSearch } from './hooks/useAutocompleteSearch';
import { useOptionsCache } from './hooks/useOptionsCache';

export type AutocompleteMultiselectInputProps<
  OptionType = RaRecord,
  DisableClearable extends boolean | undefined = false,
  SupportCreate extends boolean | undefined = false,
> = Omit<
  AutocompleteProps<OptionType, true, DisableClearable, SupportCreate>,
  'renderInput' | 'options' | 'getOptionLabel' | 'onChange'
> & {
  onChange?: (value: any[]) => void;
  optionText?: string;
  name: string;
  label: string;
  optionValue?: string;
  options?: Record<string, any>[];
  InputLabelProps?: Partial<InputLabelProps>;
  shrink?: boolean;
  source?: string;
  validate?: Validator | Validator[];
  helperText?: string | ReactElement | false;
  disabled?: boolean;
  readOnly?: boolean;
  filterToQuery?: (searchText: string) => Record<string, any>;
  getOptionLabel?: (option: any) => string;
  isRequired?: boolean;
  disabledHint?: string;
};

const defaultSx = {
  '.MuiFormControl-root': {
    marginBottom: 0,
    marginTop: 0,
  },
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    minHeight: '40px',
    paddingY: '4px',
  },
};

const defaultFilterToQuery = (value: string) => ({ name: value });

export const AutocompleteMultiselectInput: React.FC<AutocompleteMultiselectInputProps> = ({
  name,
  optionText = 'name',
  optionValue = 'id',
  label,
  options,
  defaultValue = [],
  size = 'small',
  InputLabelProps,
  shrink = true,
  getOptionLabel: getOptionLabelProp,
  disabled,
  readOnly,
  disabledHint,
  limitTags = 1,
  filterToQuery = defaultFilterToQuery,
  sx,
  renderOption,
  source,
  validate,
  helperText,
  isRequired: isRequiredProp,
  resource,
  onChange,
  ...restProps
}) => {
  const { allChoices, selectedChoices, setFilters, displayedFilters } = useChoicesContext();

  const {
    field,
    fieldState: { error, invalid, isTouched },
    formState: { isSubmitted },
  } = useInput<Identifier[]>({
    name,
    source: source ?? name,
    defaultValue,
    validate,
    onChange,
  });

  const choices: RaRecord[] = (options as RaRecord[] | undefined) ?? allChoices ?? [];
  const allKnownChoices: RaRecord[] = [...choices, ...(selectedChoices ?? [])];

  const selectedOptions = useOptionsCache({
    allKnownChoices,
    optionValue,
    selectedIds: field.value,
  });

  const { inputValue, isInputOverflowed, onInputChange, reset } = useAutocompleteSearch({
    setFilters,
    displayedFilters,
    filterToQuery,
  });

  const isDisabled = disabled || readOnly;

  const getOptionLabel = useCallback(
    (option: RaRecord | undefined): string => {
      if (!option) {
        return '';
      }
      if (getOptionLabelProp) {
        return getOptionLabelProp(option);
      }
      return option[optionText] ?? String(option[optionValue] ?? option.id ?? '');
    },
    [getOptionLabelProp, optionText, optionValue],
  );

  const isOptionEqualToValue = (option: RaRecord, val: RaRecord) =>
    option?.[optionValue] === val?.[optionValue];

  const handleChange: AutocompleteProps<RaRecord, true, false, false>['onChange'] = (_e, data) => {
    field.onChange(data.map((item) => item?.[optionValue] ?? item));
    reset();
  };

  const hasError = (isTouched || isSubmitted) && invalid;

  const renderInput: AutocompleteProps<RaRecord, true, false, false>['renderInput'] = (params) => (
    <TextField
      {...params}
      InputLabelProps={{ shrink: shrink || undefined, ...InputLabelProps }}
      error={hasError}
      helperText={
        helperText !== false || hasError ? (
          <InputHelperText error={error?.message} helperText={helperText} />
        ) : undefined
      }
      label={
        <FieldTitle
          isRequired={
            (validate as (Validator & { isRequired?: boolean }) | undefined)?.isRequired ||
            isRequiredProp
          }
          label={label}
          resource={resource}
          source={name}
        />
      }
      placeholder={disabled ? disabledHint : undefined}
      size={size}
      sx={{ marginBottom: 0 }}
      variant="outlined"
    />
  );

  const defaultRenderOption: AutocompleteProps<RaRecord, true, false, false>['renderOption'] = (
    props,
    option,
    { selected },
  ) => {
    const { key, ...rest } = props;
    return (
      <li {...rest} key={option?.[optionValue] ?? key}>
        <Checkbox checked={selected} sx={{ marginRight: '8px' }} />
        {getOptionLabel(option)}
      </li>
    );
  };

  const renderTags: AutocompleteProps<RaRecord, true, false, false>['renderTags'] = (
    tagValue,
    getTagProps,
  ) =>
    tagValue
      .map((option, index) => ({ option, index }))
      .reverse()
      .map(({ option, index }) => {
        const { key, ...rest } = getTagProps({ index });
        return (
          <Chip
            {...rest}
            key={option?.[optionValue] ?? key ?? index}
            label={getOptionLabel(option)}
            onDelete={isDisabled ? undefined : rest.onDelete}
            variant="filled"
          />
        );
      });

  return (
    <Autocomplete
      {...restProps}
      disableCloseOnSelect
      disabled={isDisabled}
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={isOptionEqualToValue}
      limitTags={isDisabled ? undefined : limitTags}
      multiple
      onBlur={reset}
      onChange={handleChange}
      onInputChange={onInputChange}
      options={choices}
      renderInput={renderInput}
      renderOption={renderOption ?? defaultRenderOption}
      renderTags={renderTags}
      sx={{
        ...defaultSx,
        '& .MuiInputBase-input': {
          minWidth: isInputOverflowed ? 'calc(100% - 12px)!important' : '0!important',
        },
        ...sx,
      }}
      value={selectedOptions}
    />
  );
};
